import React from "react";
import { CellMap } from "../../../game/CellMap";
import {
  canSweepAt,
  canVacuumAt,
  dustpanFillFraction,
  nextToGarbageCan,
} from "../../../sim/commands/cleaning-commands";
import {
  canPutDownCarriedMachine,
  explainUnpackRefusal,
} from "../../../sim/commands/machine-commands";
import { holdingBroom } from "../../../game/HeldTool";
import { MACHINE_TYPES } from "../../../game/Machine";
import { canisterFillFraction, carryingShopVac } from "../../../game/ShopVac";
import { MaterialPile } from "../../../game/GameState";
import { getMaterialFullName } from "../../../game/material-helpers";
import { liveSettingParameter } from "../../../game/machine-helpers";
import {
  blockedInteract,
  broomWithinReach,
  interactRing,
  resolveInteract,
} from "../../../game/interact";
import { chebyshevDistance } from "../../../game/Vectors";
import { HintList, HintRow } from "../../../components/shortcuts/HintList";
import { ShortcutKeys } from "../../../components/shortcuts/Kbd";
import { shopCellMap } from "../../../sim/commands/machine-commands";
import { TimeFlow } from "../../../sim/TimeFlow";
import { useGame, useShopState } from "../../useShell";
import { useTargeting } from "../useTargeting";
import { CellAnchored, PointAnchored, PointMarker } from "./OverlayLayer";

/**
 * The small cluster of key hints that follows the player: verbs aimed at
 * the floor underfoot (pick up, sweep, the shop vac) and the carrying
 * controls while a machine rides the shoulders. Pure hints — the keys do
 * the work — so it never traps the mouse.
 */
export const PlayerPrompt: React.FC = () => {
  const gameState = useShopState();
  const game = useGame();
  const cellMap = shopCellMap(game);
  const { machine: targetedMachine, pileOffset } = useTargeting();

  if (gameState.player.away) return null;

  const carried = gameState.player.carriedMachine ?? null;
  const cell = cellMap.at(gameState.player.position);
  const draggingVac = carryingShopVac(gameState);
  const standingOnVac =
    gameState.shopVac?.position != null &&
    chebyshevDistance(gameState.shopVac.position, gameState.player.position) <=
      1;
  const crateUnderfoot = !carried
    ? gameState.machineCrates.find(
        (crate) =>
          chebyshevDistance(crate.position, gameState.player.position) <= 1,
      )
    : undefined;

  // The E chip belongs to whatever the interact key resolved to — the
  // resting broom renders here at the player; floor pickups render at the
  // pile itself (below); machine and door interactions render at the
  // machine and the door.
  const interact = carried
    ? null
    : resolveInteract(gameState, cellMap, targetedMachine, pileOffset);

  // Where the pickup chip would land if the hands were free: the top of
  // the reachable ring's floor pieces. The chip still shows there, naming
  // the piece, its verb dimmed with the reason the verb is off under it.
  const blocked = carried
    ? null
    : blockedInteract(gameState, cellMap, targetedMachine);
  const blockedPickup =
    blocked?.action.kind === "pick-up-floor"
      ? { action: blocked.action, reason: blocked.reason }
      : null;

  // Whether R belongs to the targeted machine's rotate setting — the same
  // test the keyboard bindings split the key on, so the rummage hint only
  // shows when R would actually rummage.
  const rotateSettingLive =
    targetedMachine != null &&
    liveSettingParameter(targetedMachine, gameState.progression, "rotate") !=
      null;

  const rows: React.ReactNode[] = [];

  if (carried) {
    rows.push(
      // No name: the machine is on your shoulders, drawn there. Where it
      // can't be set down the verb stays, dimmed, with the reason under it.
      <HintRow
        key="put-down-machine"
        keys={<ShortcutKeys shortcut="carry-machine" />}
        reason={
          canPutDownCarriedMachine(gameState, cellMap)
            ? undefined
            : "no room here"
        }
      >
        put down
      </HintRow>,
      <HintRow key="rotate" keys={<ShortcutKeys shortcut="carry-rotate" />}>
        rotate
      </HintRow>,
    );
  } else {
    if (crateUnderfoot) {
      // The unpack key wants completely empty hands — the verb dims and
      // the chip says why when it wouldn't work.
      rows.push(
        <HintRow
          key="unpack"
          keys={<ShortcutKeys shortcut="carry-machine" />}
          reason={explainUnpackRefusal(gameState) ?? undefined}
        >
          unpack {MACHINE_TYPES[crateUnderfoot.machine.machineTypeId].name}
        </HintRow>,
      );
    }
    // The truck's bed wears its own chips, pinned over the tailgate
    // (TruckBedPrompt) — nothing for the player's cluster to carry there.
    if (interact?.kind === "pick-up-broom") {
      rows.push(
        <HintRow key="pick-up-broom" keys={<ShortcutKeys shortcut="pick-up" />}>
          pick up broom
        </HintRow>,
      );
    } else if (broomWithinReach(gameState)) {
      // The broom wants completely empty hands, a stricter bar than the
      // pickup verbs' — carrying even one board keeps the key off, so the
      // verb dims and says why instead of going quiet.
      const broomRefusal = carryingShopVac(gameState)
        ? "set the vac down first"
        : gameState.player.inventory.length > 0
          ? "empty your hands first"
          : null;
      if (broomRefusal) {
        rows.push(
          <HintRow
            key="pick-up-broom"
            keys={<ShortcutKeys shortcut="pick-up" />}
            reason={broomRefusal}
          >
            pick up broom
          </HintRow>,
        );
      }
    }
    // No chip for putting things down: it followed the player to every
    // cell, which read as a strobe. The hands strip carries the F hint
    // in its slot tooltips, and machines offer their own staging chip.
    if (holdingBroom(gameState)) {
      const panFill = dustpanFillFraction(gameState);
      const atTheCan = nextToGarbageCan(gameState, gameState.player.position);
      if (atTheCan && panFill > 0) {
        rows.push(
          <HintRow
            key="empty-pan"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
          >
            empty
          </HintRow>,
        );
      } else if (panFill >= 1) {
        // The sweep is off until the pan's emptied: the verb stays,
        // dimmed, with where to take it under it.
        rows.push(
          <HintRow
            key="sweep"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
            reason="dustpan full — empty at the can"
          >
            sweep
          </HintRow>,
        );
      } else if (canSweepAt(gameState)) {
        rows.push(
          <HintRow
            key="sweep"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
          >
            sweep
          </HintRow>,
        );
      }
    }
    if (draggingVac) {
      const fill = canisterFillFraction(gameState.shopVac!);
      const atTheCan = nextToGarbageCan(gameState, gameState.player.position);
      if (atTheCan && fill > 0) {
        rows.push(
          <HintRow
            key="empty-vac"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
          >
            empty
          </HintRow>,
        );
      } else if (fill >= 1) {
        rows.push(
          <HintRow
            key="vacuum"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
            reason="canister full — empty at the can"
          >
            vacuum
          </HintRow>,
        );
      } else if (canVacuumAt(gameState)) {
        rows.push(
          <HintRow
            key="vacuum"
            keys={<ShortcutKeys shortcut="operate-machine" />}
            hold
          >
            vacuum
          </HintRow>,
        );
      }
      rows.push(
        <HintRow key="set-vac" keys={<ShortcutKeys shortcut="put-down" />}>
          park · {Math.round(fill * 100)}%
        </HintRow>,
      );
    }
    if (interact?.kind === "pick-up-vac") {
      rows.push(
        <HintRow key="grab-vac" keys={<ShortcutKeys shortcut="pick-up" />}>
          grab vac
        </HintRow>,
      );
    } else if (standingOnVac && !draggingVac && holdingBroom(gameState)) {
      // The hose takes a hand — the grab refuses while the broom's in
      // it, so the verb dims and the chip says why under it.
      rows.push(
        <HintRow
          key="grab-vac"
          keys={<ShortcutKeys shortcut="pick-up" />}
          reason="put the broom down first"
        >
          grab vac
        </HintRow>,
      );
    }
    // The wait key earns its chip when there's something to wait *for*:
    // work running (a cure drying) while nobody spends time. Kept up
    // while the key is held, or it would vanish mid-wait.
    const speed = game.entities.getSingleton(TimeFlow).speed;
    if (
      (speed === "idle" || speed === "waiting") &&
      gameState.machines.some(
        (m) => m.operationProgress.status === "inProgress",
      )
    ) {
      rows.push(
        <HintRow key="wait" keys={<ShortcutKeys shortcut="wait" />} hold>
          pass time
        </HintRow>,
      );
    }
  }

  // Every floor piece E's ring could reach — whether or not E is aimed
  // at one right now: at a bench E means the bench until a piece is
  // pointed at, and the pointing goes through these handles.
  const ringPiles = interactRing(gameState, cellMap, targetedMachine)
    .filter((entry) => entry.kind === "floor-pile")
    .map((entry) => entry.pile);

  return (
    <>
      {/* One handle per in-reach piece, sitting on it — see PointMarker */}
      {ringPiles.map((pile) => (
        <PointMarker
          key={`anchor-${pile.material.id}`}
          point={pile.position}
          testId="pile-anchor"
          materialId={pile.material.id}
        />
      ))}
      {interact?.kind === "pick-up-floor" && (
        <PickupChip
          piles={interact.piles}
          target={interact.target}
          rummageable={
            interactRing(gameState, cellMap, targetedMachine).length > 1 &&
            !rotateSettingLive
          }
        />
      )}
      {/* Blocked hands: the same chip on the same piece, its verb dimmed
          with the reason beneath. R is dead while the hands are (the ring
          is empty), so no rummage row. */}
      {blockedPickup && (
        <PickupChip
          piles={blockedPickup.action.piles}
          target={blockedPickup.action.target}
          rummageable={false}
          reason={blockedPickup.reason}
        />
      )}
      {rows.length > 0 && (
        <CellAnchored cell={gameState.player.position}>
          <HintList testId="player-hints">{rows}</HintList>
        </CellAnchored>
      )}
    </>
  );
};

/**
 * The pickup chip sits on the pile it would grab — the same piece wearing
 * the targeting outline on the canvas, wherever it lies (long stock is
 * grabbable along its whole length; the piece underfoot may rest well off
 * to the side). The piece's name titles the chip the way a machine's name
 * titles its own (see MachineChips), leaving the key rows to be verbs; with
 * more pieces within reach it offers R to rummage — unless a machine's
 * rotate setting claims the key (the binding steps aside the same way).
 * With a `reason` the verb is off: the row dims and the reason sits under it.
 */
const PickupChip: React.FC<{
  piles: ReadonlyArray<MaterialPile>;
  target: MaterialPile;
  /** Whether R would step E on to another source — more material in
   * reach than this piece, and no rotate setting claiming the key. */
  rummageable: boolean;
  reason?: string;
}> = ({ piles, target, rummageable, reason }) => {
  const place =
    piles.length > 1
      ? ` · ${piles.indexOf(target) + 1} of ${piles.length}`
      : "";
  return (
    <PointAnchored
      point={target.position}
      placement="above"
      testId="pickup-chip"
    >
      <HintList>
        <HintRow className="text-paper-manila/60">
          {getMaterialFullName(target.material)}
          {place}
        </HintRow>
        <HintRow keys={<ShortcutKeys shortcut="pick-up" />} reason={reason}>
          pick up
        </HintRow>
        {rummageable && (
          <HintRow keys={<ShortcutKeys shortcut="cycle-pile" />}>next</HintRow>
        )}
        {/* Right-clicking the pile lays all of it out on a card, but this
            cluster is the keyboard's hints — the mouse verb is taught in
            the `?` sheet, not on a cap floating over the floor. */}
      </HintList>
    </PointAnchored>
  );
};
